<template>
    <div class="missions">
        <div class="missions__container container">
            <h2 class="missions__title">{{ title }}</h2>
            <div class="missions__top">
                <div class="missions__top-text">
                    <h3 class="missions__top-title">{{ subtitle }}</h3>
                    <p class="missions__top-description" v-html="text"></p>
                </div>
            </div>

            <div class="missions__slider swiper-container swiper js-missions-slider">
                <div class="swiper-wrapper">
                    <div class="missions__slide swiper-slide" v-for="(card, index) in projects" :key="card.id">
                        <z-card
                            theme="clipped-primary"
                            :image="card.image"
                            :is-background-image="false"
                            :content-data="{
                                title: card.title,
                                textList: card.textList,
                                tiles: card.tiles
                            }"
                        >
                        </z-card>
                    </div>
                </div>
            </div>

            <div class="missions__stats">
                <div
                    v-for="stat in stats"
                    :key="stat.id"
                    class="missions__stats-card"
                >
                    <div class="missions__stats-card-title" v-html="stat.title"/>
                    <span class="missions__stats-card-text" v-html="stat.text"></span>
                </div>
            </div>
        </div>
        <div class="missions__background--top"></div>
        <div class="missions__background--bottom"></div>
    </div>
</template>

<script setup>
import {onMounted} from 'vue';
import Swiper from 'swiper';
import {localize} from '@/utils/i18n'
import { useGetter } from '@/composable/useGetter'

const { lang } = useGetter(['lang'])

defineProps({
    title: {
        type: String,
        default: localize({
            ru: 'Устойчивое развитие',
            en: 'Sustainable development',
            cn: '可持续发展'
        }),
    },
    subtitle: {
        type: String,
        default: localize({
            ru: 'Миссия ИНК',
            en: 'INK’s mission',
            cn: '伊尔库茨克石油公司的使命'
        }),
    },
    text: {
        type: String,
        default: localize({
            ru: 'Мы&nbsp;живем на&nbsp;этой земле и&nbsp;работаем, чтобы недра Восточной Сибири служили достойному настоящему и&nbsp;уверенному будущему региона и&nbsp;его жителей. Мы&nbsp;бережно относимся к&nbsp;жизни, здоровью людей и&nbsp;уникальной природе нашего края.',
            en: 'We&nbsp;live in&nbsp;this region and work to&nbsp;ensure that the&nbsp;use of&nbsp;Eastern&nbsp;Siberia&rsquo;s mineral resources secures a&nbsp;strong present and sustainable future for&nbsp;the&nbsp;region and its people. We&nbsp;cherish the life and health of&nbsp;our people, and we care deeply about the unique nature of&nbsp;our land.',
            cn: '我们致力于确保东西伯利亚的矿产资源为该地区及其居民创造美好的现在和充满信心的未来。我们关爱生命、人类健康以及我们地区独特的自然环境。'
        }),
    },
    projects: {
        type: Array,
        default: () => []
    },
    stats: {
        type: Array,
        default: localize({
            ru: [
                {
                    title: '<span class="number">A-</span>',
                    text: 'Рейтинг в области устойчивого развития «Эксперт&nbsp;РА»',
                },
                {
                    title: '<span class="number">A-</span>',
                    text: 'Рейтинг в области устойчивого развития CCXGF',
                },
                {
                    title: '<span class="number">1,7 </span> млрд руб.',
                    text: 'природоохранные инициативы и&nbsp;платежи за&nbsp;3&nbsp;года',
                },
                {
                    title: '<span class="number">0,93</span>',
                    text: 'коэффициент частоты производственного травматизма',
                },
            ],
            en: [
                {
                    title: '<span class="number">ESG- A-</span>',
                    text: 'ESG rating Expert&nbsp;RA',
                },
                {
                    title: '<span class="number">A-</span>',
                    text: 'ESG rating CCXGF',
                },
                {
                    title: '<span class="number">1.7 </span> bln. rub.',
                    text: 'environmental initiatives and&nbsp;payments for 3&nbsp;years',
                },
                {
                    title: '<span class="number">0.93</span>',
                    text: 'work-related injury rate',
                },
            ],
            cn: [
                {
                    title: '<span class="number">ESG- A-</span>',
                    text: 'ESG评级“专家RA”',
                },
                {
                    title: '<span class="number">A-</span>',
                    text: 'ESG评级CCXGF',
                },
                {
                    title: '<span class="number">17</span>亿卢布',
                    text: '三年环保建议及资金支付',
                },
                {
                    title: '<span class="number">0,93</span>',
                    text: '工伤频率',
                },
            ],
        })
    }
})

onMounted(() => {
    const swiper = new Swiper('.js-missions-slider', {
        slidesPerView: 'auto',
        spaceBetween: 40,
        grabCursor: true,
    })
})
</script>

<style lang="scss">
.missions {
    position: relative;
    padding-top: 4.611%;
    padding-bottom: 10.2%;
    background: url(/local/templates/z-template/images/projects/missions-background.jpg) no-repeat;
    background-size: cover;
    background-position: 36% 0;
    @include breakpoint(mobile){
        padding: 40px 0 15px;

    }

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 33%;
        background: linear-gradient(180deg, rgba(2, 105, 88, 0.70) 0%, rgba(2, 105, 88, 0.69) 6.67%, rgba(3, 105, 88, 0.68) 13.33%, rgba(5, 104, 87, 0.64) 20%, rgba(8, 103, 86, 0.60) 26.67%, rgba(12, 102, 85, 0.54) 33.33%, rgba(16, 101, 83, 0.47) 40%, rgba(20, 100, 82, 0.39) 46.67%, rgba(25, 99, 80, 0.31) 53.33%, rgba(29, 98, 79, 0.23) 60%, rgba(33, 97, 77, 0.16) 66.67%, rgba(37, 96, 76, 0.10) 73.33%, rgba(40, 95, 75, 0.06) 80%, rgba(42, 94, 74, 0.02) 86.67%, rgba(43, 94, 74, 0.01) 93.33%, rgba(43, 94, 74, 0.00) 100%);
        pointer-events: none;
    }

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, rgba(2, 105, 88, 0.50) 40.51%, rgba(2, 105, 88, 0.50) 44.48%, rgba(3, 105, 88, 0.48) 48.44%, rgba(5, 104, 87, 0.46) 52.41%, rgba(8, 103, 86, 0.43) 56.38%, rgba(12, 102, 85, 0.38) 60.34%, rgba(16, 101, 83, 0.33) 64.31%, rgba(20, 100, 82, 0.28) 68.27%, rgba(25, 99, 80, 0.22) 72.24%, rgba(29, 98, 79, 0.17) 76.21%, rgba(33, 97, 77, 0.12) 80.17%, rgba(37, 96, 76, 0.07) 84.14%, rgba(40, 95, 75, 0.04) 88.1%, rgba(42, 94, 74, 0.02) 92.07%, rgba(43, 94, 74, 0.00) 96.03%, rgba(43, 94, 74, 0.00) 100%);
        pointer-events: none;
    }

    &__container {
        position: relative;
        z-index: 1;
    }

    &__title {
        margin-bottom: 38px;
        @include typo-level(2XL);
        color: var(--color-white);

        @include breakpoint(tablet){
            @include margin-level(bottom, M);
            @include typo-level(3XL);
            line-height: 125%;
        }
    }

    &__top {
        @include margin-level(bottom, XL);
        display: flex;
        justify-content: space-between;
        @include gap-level(M);

        &-text {
            width: 64%;
            @include breakpoint(v-tablet){
                width: 100%;
            }
        }

        &-title {
            @include margin-level(bottom, L);
            font-size: 48px;
            font-weight: 500;
            line-height: 120%;
            color: var(--color-white);

            @include breakpoint(v-tablet){
                font-size: 32px;
            }

            @include breakpoint(mobile){
                margin-bottom: 20px;
                font-size: 26px;
            }
        }

        &-description {
            margin: 0;
            font-size: 30px;
            line-height: 150%;
            letter-spacing: 0.3px;
            color: var(--color-white);

            @include breakpoint(v-tablet){
                @include typo-level(M);
            }

            @include breakpoint(mobile){
                width: 312px;
            }
        }
    }

    &__slider{
        overflow: visible;
    }

    &__slide{
        width: fit-content;
        height: auto;
        & .z-card{
            height: 100%;

            &__content-title{
              @include breakpoint(mobile){
                  font-size: 18px;
              }
            }
        }
    }

    &__stats{
        @include margin-level(top, 2XL);
        display: flex;
        justify-content: space-between;
        @include gap-level(M);

        @include breakpoint(v-tablet){
            margin: 0;
            padding: 40px 0;
            flex-direction: column;
        }

        &-card{
            max-width: 374px;

            @include breakpoint(mobile){
                max-width: 250px;
            }

            &-title{
                margin: 0 0 8px;
                font-size: 40px;
                font-weight: 500;
                line-height: 100%;
                letter-spacing: -0.8px;
                color: var(--color-white);
                & .number{
                    font-size: 80px;
                    font-weight: 500;
                    line-height: 100%;
                    letter-spacing: -1.6px;
                    white-space: nowrap;

                    @include breakpoint(v-tabet){
                        font-size: 60px;
                    }
                    @include breakpoint(mobile){
                        font-size: 48px;
                        letter-spacing: -0.96px;
                    }
                }
            }
            &-text{
                @include typo-level(XL);
                line-height: 120%;
                color: var(--color-white);

                @include breakpoint(mobile){
                    font-size: 16px;
                    font-weight: 600;
                }
            }
        }
    }
    &__background{

        &--top{
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 46%;
            background: linear-gradient(180deg, rgba(2, 105, 88, 0.70) 48.17%, rgba(2, 105, 88, 0.69) 54.4%, rgba(3, 105, 88, 0.68) 63.62%, rgba(5, 104, 87, 0.64) 68.85%, rgba(8, 103, 86, 0.60) 76.08%, rgba(12, 102, 85, 0.54) 79.31%, rgba(16, 101, 83, 0.47) 87.54%, rgba(20, 100, 82, 0.39) 91.77%, rgba(25, 99, 80, 0.31) 97.99%, rgba(29, 98, 79, 0.23) 104.22%, rgba(33, 97, 77, 0.16) 110.45%, rgba(37, 96, 76, 0.10) 116.68%, rgba(40, 95, 75, 0.06) 122.91%, rgba(42, 94, 74, 0.02) 129.14%, rgba(43, 94, 74, 0.01) 135.36%, rgba(43, 94, 74, 0.00) 141.59%);            pointer-events: none;
        }

        &--bottom{
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 33%;
            background: linear-gradient(0deg, rgba(2, 105, 88, 0.70) 0%, rgba(2, 105, 88, 0.69) 6.67%, rgba(3, 105, 88, 0.68) 13.33%, rgba(5, 104, 87, 0.64) 20%, rgba(8, 103, 86, 0.60) 26.67%, rgba(12, 102, 85, 0.54) 33.33%, rgba(16, 101, 83, 0.47) 40%, rgba(20, 100, 82, 0.39) 46.67%, rgba(25, 99, 80, 0.31) 53.33%, rgba(29, 98, 79, 0.23) 60%, rgba(33, 97, 77, 0.16) 66.67%, rgba(37, 96, 76, 0.10) 73.33%, rgba(40, 95, 75, 0.06) 80%, rgba(42, 94, 74, 0.02) 86.67%, rgba(43, 94, 74, 0.01) 93.33%, rgba(43, 94, 74, 0.00) 100%);
            pointer-events: none;
        }
    }
}
</style>
